import {
  Button,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Text,
  Textarea,
  useCustomToast,
  VStack,
} from '@elkaso-app/web-design';
import { useGetDedDetailByIdApi, useUpdateDedDetailApi } from 'apis/crm-ded-details';
import React, { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';

interface EditDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  detailId: number;
  businessName: string;
}

interface FormData {
  businessName: string;
  englishName?: string;
  arabicName?: string;
  licenseNumber: string;
  licenseStatus?: string;
  legalType?: string;
  expiryDate?: string;
  activities?: string;
  telephone?: string;
  fax?: string;
  mobile?: string;
  poBox?: string;
  email?: string;
  address?: string;
  area?: string;
  subArea?: string;
  community?: string;
  landmark?: string;
  parcelId?: string;
  street?: string;
  building?: string;
  floor?: string;
  unit?: string;
  gpsCoordinates?: string;
}

const LICENSE_STATUS_OPTIONS = [
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
  { value: 'Suspended', label: 'Suspended' },
  { value: 'Expired', label: 'Expired' },
  { value: 'Pending', label: 'Pending' },
];

const LEGAL_TYPE_OPTIONS = [
  { value: 'Limited Liability Company', label: 'Limited Liability Company' },
  { value: 'Sole Proprietorship', label: 'Sole Proprietorship' },
  { value: 'Partnership', label: 'Partnership' },
  { value: 'Corporation', label: 'Corporation' },
  { value: 'Other', label: 'Other' },
];

export const EditDetailModal: React.FC<EditDetailModalProps> = ({ isOpen, onClose, detailId, businessName }) => {
  const toast = useCustomToast();

  // Fetch detail data
  const { data: detailData, isLoading: isLoadingDetail } = useGetDedDetailByIdApi(detailId, {
    enabled: isOpen && !!detailId,
  });

  // Update mutation
  const { mutate: updateDetail, isLoading: isUpdating } = useUpdateDedDetailApi({
    onSuccess: () => {
      toast({
        title: 'DED Detail Updated!',
        description: 'The DED detail has been successfully updated.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      onCloseModal();
    },
    onError: (error: any) => {
      toast({
        title: 'Update Failed',
        description: error?.response?.data?.message || 'Failed to update DED detail. Please try again.',
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>();

  // Reset form when detail data is loaded
  useEffect(() => {
    if (isOpen && detailData) {
      reset({
        businessName: detailData.businessName || '',
        englishName: detailData.englishName || '',
        arabicName: detailData.arabicName || '',
        licenseNumber: detailData.licenseNumber || '',
        licenseStatus: detailData.licenseStatus || '',
        legalType: detailData.legalType || '',
        expiryDate: detailData.expiryDate ? detailData.expiryDate.split('T')[0] : '',
        activities: detailData.activities || '',
        telephone: detailData.telephone || '',
        fax: detailData.fax || '',
        mobile: detailData.mobile || '',
        poBox: detailData.poBox || '',
        email: detailData.email || '',
        address: detailData.address || '',
        area: detailData.area || '',
        subArea: detailData.subArea || '',
        community: detailData.community || '',
        landmark: detailData.landmark || '',
        parcelId: detailData.parcelId || '',
        street: detailData.street || '',
        building: detailData.building || '',
        floor: detailData.floor || '',
        unit: detailData.unit || '',
        gpsCoordinates: detailData.gpsCoordinates || '',
      });
    }
  }, [isOpen, detailData, reset]);

  // Memoized close handler
  const onCloseModal = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  // Memoized submit handler
  const onSubmit = useCallback(
    (data: FormData) => {
      const payload = {
        ...data,
        expiryDate: data.expiryDate || undefined,
      };

      updateDetail({ id: detailId, data: payload });
    },
    [detailId, updateDetail]
  );

  return (
    <Modal isOpen={isOpen} onClose={onCloseModal} size='6xl' closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <VStack align='start' spacing={1}>
            <Text>Edit DED Detail</Text>
            <Text fontSize='md' fontWeight='normal' color='gray.600'>
              {businessName}
            </Text>
          </VStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody pb={6}>
          {isLoadingDetail ? (
            <div>Loading detail information...</div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)}>
              <VStack spacing={6} align='stretch'>
                {/* Basic Information */}
                <VStack spacing={4} align='stretch'>
                  <Text fontSize='lg' fontWeight='bold' color='blue.600'>
                    Basic Information
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl isInvalid={!!errors.businessName} isRequired>
                      <FormLabel>Business Name</FormLabel>
                      <Input
                        {...register('businessName', {
                          required: 'Business name is required',
                          minLength: { value: 2, message: 'Business name must be at least 2 characters' },
                        })}
                        placeholder='Enter business name'
                      />
                      <FormErrorMessage>{errors.businessName?.message}</FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={!!errors.englishName}>
                      <FormLabel>English Name</FormLabel>
                      <Input {...register('englishName')} placeholder='Enter English name' />
                      <FormErrorMessage>{errors.englishName?.message}</FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={!!errors.arabicName}>
                      <FormLabel>Arabic Name</FormLabel>
                      <Input {...register('arabicName')} placeholder='Enter Arabic name' />
                      <FormErrorMessage>{errors.arabicName?.message}</FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={!!errors.legalType}>
                      <FormLabel>Legal Type</FormLabel>
                      <Select {...register('legalType')} placeholder='Select legal type'>
                        {LEGAL_TYPE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Select>
                      <FormErrorMessage>{errors.legalType?.message}</FormErrorMessage>
                    </FormControl>
                  </SimpleGrid>
                </VStack>

                <Divider />

                {/* License Information */}
                <VStack spacing={4} align='stretch'>
                  <Text fontSize='lg' fontWeight='bold' color='green.600'>
                    License Information
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl isInvalid={!!errors.licenseNumber} isRequired>
                      <FormLabel>License Number</FormLabel>
                      <Input
                        {...register('licenseNumber', {
                          required: 'License number is required',
                          minLength: { value: 3, message: 'License number must be at least 3 characters' },
                        })}
                        placeholder='Enter license number'
                      />
                      <FormErrorMessage>{errors.licenseNumber?.message}</FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={!!errors.licenseStatus}>
                      <FormLabel>License Status</FormLabel>
                      <Select {...register('licenseStatus')} placeholder='Select license status'>
                        {LICENSE_STATUS_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Select>
                      <FormErrorMessage>{errors.licenseStatus?.message}</FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={!!errors.expiryDate}>
                      <FormLabel>Expiry Date</FormLabel>
                      <Input type='date' {...register('expiryDate')} />
                      <FormErrorMessage>{errors.expiryDate?.message}</FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={!!errors.activities} gridColumn={{ base: '1', md: '1 / -1' }}>
                      <FormLabel>Activities</FormLabel>
                      <Textarea {...register('activities')} placeholder='Enter business activities' rows={3} />
                      <FormErrorMessage>{errors.activities?.message}</FormErrorMessage>
                    </FormControl>
                  </SimpleGrid>
                </VStack>

                <Divider />

                {/* Contact Information */}
                <VStack spacing={4} align='stretch'>
                  <Text fontSize='lg' fontWeight='bold' color='purple.600'>
                    Contact Information
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl isInvalid={!!errors.mobile}>
                      <FormLabel>Mobile</FormLabel>
                      <Input {...register('mobile')} placeholder='Enter mobile number' />
                      <FormErrorMessage>{errors.mobile?.message}</FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={!!errors.telephone}>
                      <FormLabel>Telephone</FormLabel>
                      <Input {...register('telephone')} placeholder='Enter telephone number' />
                      <FormErrorMessage>{errors.telephone?.message}</FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={!!errors.email}>
                      <FormLabel>Email</FormLabel>
                      <Input type='email' {...register('email')} placeholder='Enter email address' />
                      <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={!!errors.fax}>
                      <FormLabel>Fax</FormLabel>
                      <Input {...register('fax')} placeholder='Enter fax number' />
                      <FormErrorMessage>{errors.fax?.message}</FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={!!errors.poBox}>
                      <FormLabel>PO Box</FormLabel>
                      <Input {...register('poBox')} placeholder='Enter PO Box' />
                      <FormErrorMessage>{errors.poBox?.message}</FormErrorMessage>
                    </FormControl>
                  </SimpleGrid>
                </VStack>

                <Divider />

                {/* Location Information */}
                <VStack spacing={4} align='stretch'>
                  <Text fontSize='lg' fontWeight='bold' color='orange.600'>
                    Location Information
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl isInvalid={!!errors.address} gridColumn={{ base: '1', md: '1 / -1' }}>
                      <FormLabel>Address</FormLabel>
                      <Textarea {...register('address')} placeholder='Enter full address' rows={2} />
                      <FormErrorMessage>{errors.address?.message}</FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={!!errors.area}>
                      <FormLabel>Area</FormLabel>
                      <Input {...register('area')} placeholder='Enter area' />
                      <FormErrorMessage>{errors.area?.message}</FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={!!errors.subArea}>
                      <FormLabel>Sub Area</FormLabel>
                      <Input {...register('subArea')} placeholder='Enter sub area' />
                      <FormErrorMessage>{errors.subArea?.message}</FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={!!errors.community}>
                      <FormLabel>Community</FormLabel>
                      <Input {...register('community')} placeholder='Enter community' />
                      <FormErrorMessage>{errors.community?.message}</FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={!!errors.landmark}>
                      <FormLabel>Landmark</FormLabel>
                      <Input {...register('landmark')} placeholder='Enter landmark' />
                      <FormErrorMessage>{errors.landmark?.message}</FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={!!errors.street}>
                      <FormLabel>Street</FormLabel>
                      <Input {...register('street')} placeholder='Enter street' />
                      <FormErrorMessage>{errors.street?.message}</FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={!!errors.building}>
                      <FormLabel>Building</FormLabel>
                      <Input {...register('building')} placeholder='Enter building' />
                      <FormErrorMessage>{errors.building?.message}</FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={!!errors.floor}>
                      <FormLabel>Floor</FormLabel>
                      <Input {...register('floor')} placeholder='Enter floor' />
                      <FormErrorMessage>{errors.floor?.message}</FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={!!errors.unit}>
                      <FormLabel>Unit</FormLabel>
                      <Input {...register('unit')} placeholder='Enter unit' />
                      <FormErrorMessage>{errors.unit?.message}</FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={!!errors.parcelId}>
                      <FormLabel>Parcel ID</FormLabel>
                      <Input {...register('parcelId')} placeholder='Enter parcel ID' />
                      <FormErrorMessage>{errors.parcelId?.message}</FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={!!errors.gpsCoordinates}>
                      <FormLabel>GPS Coordinates</FormLabel>
                      <Input {...register('gpsCoordinates')} placeholder='Enter GPS coordinates' />
                      <FormErrorMessage>{errors.gpsCoordinates?.message}</FormErrorMessage>
                    </FormControl>
                  </SimpleGrid>
                </VStack>

                <Divider />

                {/* Action Buttons */}
                <HStack justify='flex-end' spacing={3}>
                  <Button variant='outline' onClick={onCloseModal} disabled={isUpdating}>
                    Cancel
                  </Button>
                  <Button type='submit' colorScheme='blue' isLoading={isUpdating} loadingText='Updating...'>
                    Update DED Detail
                  </Button>
                </HStack>
              </VStack>
            </form>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
